import { Injectable, Inject } from '@angular/core';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { LoadedConfiguratorsDataValue } from '@icc/shared/configurators/configurators-data.service';
import { core, logger } from '@icc/shared/helpers';
import { Common } from '@icc/shared/helpers';
import { ModalService } from '@icc/configurator/shared';
import { ConfiguratorsDataService, EventBusService, BaseIssuesService } from '@icc/shared';
import { IssueLevel } from '@icc/shared/helpers';
import { ValidationService } from '@icc/construction-elements/configurator';
import { APP_CONFIG, AppConfig, AppConfigFactory } from '@icc/shared/config';
import { SealColorsDefaultsService } from './seal-colors-defaults.service';
import { SealColorsPageComponent } from '../../seal-colors-page/seal-colors-page.component';
import { ConfigurationsService } from '@icc/configurator/shared';

@Injectable()
export class SealColorsService {
    allColors = [];
    colors = [];

    private loadedData = false;

    constructor(
        private eventBusService: EventBusService,
        private modalService: ModalService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private issuesService: BaseIssuesService,
        private validationService: ValidationService,
        private sealColorsDefaultsService: SealColorsDefaultsService,
        private configurationsService: ConfigurationsService<'window'>,
        private configuratorsDataService: ConfiguratorsDataService
    ) {
        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.init();
        });

        eventBusService.subscribe<LoadedConfiguratorsDataValue>(['setConstructionColor', 'changedSashes'], data => {
            if (
                WindowActiveConfiguration.is(data.activeConfiguration)
                && WindowActiveConfiguration.is(data.defaultConfiguration)
            ) {
                if(
                    !this.config().IccConfig.Configurators.sealColorDependOnConstructionColor
                    || (data.activeConfiguration as WindowActiveConfiguration).Colors.sash.outer
                ) {
                    this.loadMatchingColors(data.activeConfiguration as WindowActiveConfiguration);
                    this.setDefaultColor(
                        data.activeConfiguration as WindowActiveConfiguration,
                        data.defaultConfiguration as WindowActiveConfiguration,
                        data.user
                    );
                }
            }
        });

        eventBusService.subscribe<LoadedConfiguratorsDataValue>('loadedConfiguratorsData', data => {
            if (config().IccConfig.Configurators.sealColorSelect) {
                this.loadColors(data.value);
                if (
                    data.activeConfiguration
                    && WindowActiveConfiguration.is(data.activeConfiguration)
                    && WindowActiveConfiguration.is(data.defaultConfiguration)
                ) {
                    this.loadMatchingColors(data.activeConfiguration as WindowActiveConfiguration);
                    this.setDefaultColor(
                        data.activeConfiguration as WindowActiveConfiguration,
                        data.defaultConfiguration as WindowActiveConfiguration,
                        data.user
                    );
                }
            }
        });

        eventBusService.subscribe<LoadedConfiguratorsDataValue>(['setSystem'], data => {
            try {
                if(
                    !this.config().IccConfig.Configurators.sealColorDependOnConstructionColor
                    || (data.activeConfiguration as WindowActiveConfiguration).Colors.sash.outer
                ) {
                    this.loadMatchingColors(data.activeConfiguration as WindowActiveConfiguration);
                    this.setDefaultColor(
                        data.activeConfiguration as WindowActiveConfiguration,
                        data.defaultConfiguration as WindowActiveConfiguration,
                        data.user
                    );
                }
            } catch (err) {
                logger.error(err);
            }
        });
    }

    init() {
        if (this.config().IccConfig.Configurators.sealColorSelect) {
            this.loadColors(this.configuratorsDataService.data);
        }
    }

    loadColors(data) {
        this.allColors = data.windowSealColors || [];
    }

    loadMatchingColors(conf: WindowActiveConfiguration) {
        if (this.allColors.length > 0 && conf.System.id) {
            this.colors = this.allColors.filter(
                color =>
                    color.window_lines_ids
                    && color.window_lines_ids.map(Number).indexOf(Number(conf.System.id)) > -1
            );
            if(this.config().IccConfig.Configurators.sealColorDependOnConstructionColor) {
                this.colors = this.getColors();
            }
            this.loadedData = true;
        }
    }

    setDefaultColor(conf: WindowActiveConfiguration, defaultConf, user) {
        if (!this.loadedData || !conf.System || !conf.System.id) {
            return;
        }
        if (this.colors.length === 0) {
            this.issuesService.registerDataProblem(
                'no-seal-colors',
                'Brak kolorów uszczelek!',
                conf,
                {
                    level: IssueLevel.FATAL,
                    extra: {
                        systemId: Number(conf.System.id)
                    }
                }
            );
            this.validationService.invalid(conf, 'sealColor');
            return;
        }
        this.issuesService.unregister('no-seal-colors', conf);
        const shouldBeChanged =
            !Common.isObject(conf.SealColor)
            || !conf.SealColor.id
            || conf.SealColor.isDefault
            || (conf.SealColor.window_lines_ids
                && conf.SealColor.window_lines_ids.map(Number).indexOf(Number(conf.System.id))
                    === -1)
            || (this.config().IccConfig.Configurators.sealColorDependOnConstructionColor && !this.checkSealColor(conf, conf.SealColor.id));
        if (shouldBeChanged) {
            const defaultColor = this.sealColorsDefaultsService.getDefaultColor(
                this.colors,
                conf,
                user,
                defaultConf,
                this.configuratorsDataService.data.windowColorsAll
            );
            this.setColor(defaultColor, true, conf, defaultConf);
        } else {
            Object.assign(conf, this.validationService.valid(conf, 'sealColor'));
            this.eventBusService.post({
                key: 'setSealColor',
                value: {
                    colorId: conf.SealColor.id,
                },
                conf,
                defaultConf,
            });
        }
    }
    setColor(color, isDefault = false, conf: WindowActiveConfiguration, defaultConf) {
        color = core.copy(color);
        color.isDefault = isDefault;
        conf.SealColor = core.copy(color);
        defaultConf.SealColor = color;
        Object.assign(conf, this.validationService.valid(conf, 'sealColor'));
        this.eventBusService.post({
            key: 'setSealColor',
            value: {
                colorId: color.id,
            },
            conf,
            defaultConf,
        });
    }
    openModal(conf: WindowActiveConfiguration, defaultConf) {
        this.modalService
            .open({
                templateUrl: 'modalSealColor.html',
                controller: 'ModalSealColorCtrl as msealcolor',
                pageComponent: SealColorsPageComponent,
                resolve: {
                    colors: () => this.colors,
                    selColor: () => conf.SealColor,
                },
            })
            .result.then(selectedColor => {
                if (selectedColor) {
                    this.setColor(selectedColor, false, conf, defaultConf);
                }
            });
    }

    getColors(conf = this.configurationsService.conf.Current) {
        return this.colors.filter(color => this.checkSealColor(conf, color.id))
    }

    checkSealColor(conf = this.configurationsService.conf.Current, sealColorId) {
        const colors = this.configuratorsDataService.data.windowColorsAll;
        const sashOuterColor = conf.Colors.sash.outer;
        const outerColor =  colors.find(c => Number(c.id) === Number(sashOuterColor?.id));

        if(!sealColorId) {
            return false;
        }
        if(outerColor && outerColor.sealColorsIds.indexOf(Number(sealColorId)) > -1) {
            return true;
        }
        return false;
    }

}
